import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useScrollAnimation, useStaggerAnimation } from "@/hooks/useScrollAnimation";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";

const companyInfo = [
  { label: "会社名", value: "株式会社FD" },
  { label: "英語表記", value: "Future Diver Co., Ltd." },
  { label: "設立", value: "2026年" },
  { label: "代表者", value: "代表取締役 坂田達彦" },
  { label: "法人番号", value: "8180301040637" },
  { label: "本社所在地", value: "〒448-0002 愛知県刈谷市一里山町中本山67番地10" },
  { label: "TEL", value: "080-6952-9336" },
  { label: "事業内容", value: "訪問介護事業、障害福祉サービス事業、その他生活支援サービス" },
];

const history = [
  { year: "2026", title: "株式会社FD 設立", desc: "愛知県刈谷市にて株式会社FDを設立。「未来という海を、ともに泳ぎ進む」をコンセプトに事業をスタート。" },
  { year: "2026", title: "ヘルパーステーションみりん 開設", desc: "刈谷市井ケ谷町に訪問介護事業所「ヘルパーステーションみりん」を開設。地域の高齢者・障害者支援を開始。" },
  { year: "2026", title: "障害福祉サービス事業 開始", desc: "訪問介護に加え、障害福祉サービス（居宅介護・重度訪問介護）の提供を開始。支援の幅を拡大。" },
  { year: "2026", title: "対応エリア拡大", desc: "刈谷市内全域および近隣市町村への対応エリアを拡大。より多くの方々へサービスを届けられる体制を整備。" },
  { year: "2026", title: "さらなる未来へ", desc: "福祉・介護の枠を超えた新たなサービス展開を計画中。地域社会とともに成長し続けます。" },
];

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: missionRef, isVisible: missionVisible } = useScrollAnimation({ threshold: 0.15 });
  const { ref: tableRef, isVisible: tableVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: historyTitleRef, isVisible: historyTitleVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: historyRef, visibleItems: historyItems } = useStaggerAnimation(history.length, { threshold: 0.1 });

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative h-[340px] md:h-[420px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20japanese%20city%20kariya%20aichi%2C%20modern%20urban%20landscape%2C%20clear%20blue%20sky%2C%20green%20parks%2C%20residential%20and%20commercial%20mix%2C%20bright%20daytime%2C%20wide%20panoramic%20shot%2C%20photorealistic&width=1440&height=500&seq=about-hero-01&orientation=landscape"
          alt="株式会社FD 会社概要"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div
          className="absolute inset-0 flex items-center justify-center"
          ref={heroRef as React.RefObject<HTMLDivElement>}
        >
          <div className="text-center">
            <p
              className="text-white/70 text-xs font-['Poppins'] tracking-widest mb-3"
              style={{
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? "translateY(0)" : "translateY(20px)",
                transition: "opacity 0.7s ease, transform 0.7s ease",
              }}
            >
              COMPANY
            </p>
            <h1
              className="text-3xl md:text-5xl font-bold text-white tracking-wide"
              style={{
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? "translateY(0)" : "translateY(25px)",
                transition: "opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s",
              }}
            >
              会社概要
            </h1>
            <div
              className="flex items-center justify-center gap-2 mt-4 text-xs text-white/60"
              style={{
                opacity: heroVisible ? 1 : 0,
                transition: "opacity 0.8s ease 0.3s",
              }}
            >
              <Link to="/" className="hover:text-white transition-colors cursor-pointer">TOP</Link>
              <span>›</span>
              <span className="text-white/90">会社概要</span>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-[#f4fbfc]" ref={missionRef as React.RefObject<HTMLElement>}>
        <div className="max-w-[1240px] mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-14">
            <div className="flex-1">
              <p
                className="text-[#3ab5c6] text-xs font-['Poppins'] tracking-widest mb-3"
                style={{
                  opacity: missionVisible ? 1 : 0,
                  transform: missionVisible ? "translateX(0)" : "translateX(-20px)",
                  transition: "opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s",
                }}
              >
                OUR MISSION
              </p>
              <h2
                className="text-2xl md:text-3xl font-bold text-gray-800 leading-snug mb-6"
                style={{
                  opacity: missionVisible ? 1 : 0,
                  transform: missionVisible ? "translateY(0)" : "translateY(20px)",
                  transition: "opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s",
                }}
              >
                未来という海を、<br />ともに泳ぎ進む。
              </h2>
              <div
                className="space-y-4 text-sm text-gray-600 leading-relaxed"
                style={{
                  opacity: missionVisible ? 1 : 0,
                  transform: missionVisible ? "translateY(0)" : "translateY(20px)",
                  transition: "opacity 0.8s ease 0.35s, transform 0.8s ease 0.35s",
                }}
              >
                <p>
                  FDは「<span className="text-[#3ab5c6] font-medium">Future Diver</span>」の頭文字。未来を広大な海と捉え、その海を恐れず好奇心を持って泳ぎ進むダイバーのように私たちは、お客様や地域社会とともに、新しい未来へと向かっていきます。
                </p>
                <p>
                  現在は愛知県刈谷市を拠点に、訪問介護事業所「ヘルパーステーションみりん」を運営しています。一人ひとりの暮らしに寄り添い、その人らしい毎日を支えることが、私たちの出発点です。
                </p>
                <p>
                  福祉・介護の枠を超え、様々な分野で人々の生活を豊かにするサービスへ。これはまだ、始まりに過ぎません。
                </p>
              </div>
            </div>
            <div
              className="flex-1 w-full"
              style={{
                opacity: missionVisible ? 1 : 0,
                transform: missionVisible ? "translateX(0)" : "translateX(40px)",
                transition: "opacity 0.9s ease 0.3s, transform 0.9s ease 0.3s",
              }}
            >
              <div className="bg-white p-3 md:p-5 rounded-2xl">
                <div className="h-80 md:h-[520px] rounded-xl overflow-hidden relative">
                  <img
                    src="https://static.readdy.ai/image/e643b173cc54a8b3f926f18f1e409a57/7589743477f5019e3b5a767269406922.jpeg"
                    alt="FDのミッション"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

      </section>

      {/* Company Info Table */}
      <section className="py-20 bg-[#f0fafb]" ref={tableRef as React.RefObject<HTMLElement>}>
        <div className="max-w-[900px] mx-auto px-6">
          <div
            className="mb-10 text-center"
            style={{
              opacity: tableVisible ? 1 : 0,
              transform: tableVisible ? "translateY(0)" : "translateY(25px)",
              transition: "opacity 0.7s ease, transform 0.7s ease",
            }}
          >
            <p className="text-[#3ab5c6] text-xs font-['Poppins'] tracking-widest mb-2">COMPANY INFO</p>
            <h2 className="text-2xl font-bold text-gray-800">会社情報</h2>
          </div>
          <div
            className="bg-white rounded-2xl overflow-hidden"
            style={{
              opacity: tableVisible ? 1 : 0,
              transform: tableVisible ? "translateY(0)" : "translateY(30px)",
              transition: "opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s",
            }}
          >
            {companyInfo.map((row, i) => (
              <div
                key={i}
                className={`flex flex-col sm:flex-row ${i !== companyInfo.length - 1 ? "border-b border-gray-100" : ""}`}
              >
                <div className="sm:w-40 px-6 py-4 bg-[#f7fcfd] text-xs font-medium text-gray-500 flex items-center whitespace-nowrap">
                  {row.label}
                </div>
                <div className="flex-1 px-6 py-4 text-sm text-gray-700">
                  {row.label === "TEL" ? (
                    <a href={`tel:${row.value}`} className="text-[#3ab5c6] hover:underline cursor-pointer">{row.value}</a>
                  ) : row.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* History */}
      <section className="py-20 bg-white">
        <div className="max-w-[900px] mx-auto px-6">
          <div
            className="mb-12 text-center"
            ref={historyTitleRef as React.RefObject<HTMLDivElement>}
            style={{
              opacity: historyTitleVisible ? 1 : 0,
              transform: historyTitleVisible ? "translateY(0)" : "translateY(25px)",
              transition: "opacity 0.7s ease, transform 0.7s ease",
            }}
          >
            <p className="text-[#3ab5c6] text-xs font-['Poppins'] tracking-widest mb-2">HISTORY</p>
            <h2 className="text-2xl font-bold text-gray-800">沿革</h2>
          </div>
          <div className="relative" ref={historyRef as React.RefObject<HTMLDivElement>}>
            <div className="absolute left-[72px] top-0 bottom-0 w-px bg-[#d6eff3]"></div>
            <div className="space-y-8">
              {history.map((item, i) => (
                <div
                  key={i}
                  className="flex gap-6 items-start"
                  style={{
                    opacity: historyItems[i] ? 1 : 0,
                    transform: historyItems[i] ? "translateX(0)" : "translateX(-30px)",
                    transition: "opacity 0.7s ease, transform 0.7s ease",
                  }}
                >
                  <div className="w-[72px] shrink-0 text-right">
                    <span className="text-sm font-bold text-[#3ab5c6] font-['Poppins']">{item.year}</span>
                  </div>
                  <div className="relative pt-0.5">
                    <div className="absolute -left-[25px] top-1.5 w-3 h-3 rounded-full bg-[#3ab5c6] border-2 border-white ring-2 ring-[#3ab5c6]/30"></div>
                    <h3 className="text-sm font-bold text-gray-800 mb-1">{item.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Schema.org AboutPage */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "会社概要｜株式会社FD",
          "description": "愛知県刈谷市の株式会社FDの会社概要。訪問介護事業・障害福祉サービス事業を展開。ヘルパーステーションみりんを運営しています。",
          "url": "https://future-diver.jp/about",
          "mainEntity": {
            "@type": "Organization",
            "name": "株式会社FD",
            "alternateName": "Future Diver Co., Ltd.",
            "url": "https://future-diver.jp/",
            "logo": "https://future-diver.jp/logo.png",
            "description": "愛知県刈谷市を拠点に、訪問介護事業・障害福祉サービス事業を展開する株式会社FD。",
            "address": {
              "@type": "PostalAddress",
              "postalCode": "448-0002",
              "addressRegion": "愛知県",
              "addressLocality": "刈谷市",
              "streetAddress": "一里山町中本山67番地10"
            },
            "telephone": "+81-80-6952-9336",
            "foundingDate": "2026",
            "founder": {
              "@type": "Person",
              "name": "坂田達彦"
            },
            "areaServed": {
              "@type": "City",
              "name": "刈谷市"
            }
          }
        })}
      </script>
    </div>
  );
}
